import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import { BOTONES_CORREOS_IA, FICHAS_CORREOS_IA } from "./redaccionComun";

/**
 * Ayuda de «Ticket en Jimi» (`components/AbrirTicketJimiModal`): crear el
 * ticket en su portal, registrar a mano un número que ya existe, o corregir
 * ese número (`editando`).
 */
export function ayudaTicketJimi({
  modo,
  editando,
  sinEquipos = false,
  iaHabilitada,
}: {
  modo: "crear" | "manual";
  editando: boolean;
  /** El caso no trae IMEIs: la ventana solo registra el número (misma condición que el modal). */
  sinEquipos?: boolean;
  iaHabilitada: boolean;
}): ContenidoAyuda {
  const botones: ItemAyuda[] = [];
  if (!editando && !sinEquipos) {
    botones.push({
      label: "Crear nuevo / Ya tengo el número",
      text: "Cambia entre crear el ticket en Jimi y registrar el número de uno que abriste a mano.",
    });
  }

  if (modo === "manual") {
    botones.push(
      {
        label: "Guardar número",
        text: "Registra el número tal cual (letras, números y guiones) para que el caso siga ese ticket. Al guardarlo se busca en Jimi: si no lo encuentra te avisa, y queda guardado igual para que lo corrijas con ✏️ junto al número. No cambia un ticket que se creó con el botón «Crear en Jimi». Sale gris sin número.",
      },
      { label: "Cancelar", text: "Cierra sin guardar." },
    );
    return {
      manual: "jimi-abrir",
      intro: (
        <p>
          {editando
            ? "Corrige el número de ticket de Jimi que se capturó a mano."
            : sinEquipos
              ? "Este caso no tiene equipos (IMEIs), y desde aquí el ticket se arma con los equipos del caso, así que no se puede crear. Ábrelo a mano en el portal de Jimi y registra aquí su número: el caso lo sigue como si lo hubieras creado desde aquí."
              : "¿Ya abriste el ticket a mano en el portal de Jimi? Registra aquí su número y el caso lo sigue como si lo hubieras creado desde aquí."}
        </p>
      ),
      secciones: [{ titulo: "Qué hace cada botón", items: botones }],
    };
  }

  botones.push({
    label: "Reintentar",
    text: "Sale si no se pudo preparar el ticket o cargar la lista de modelos de Jimi: lo vuelve a intentar.",
  });
  if (iaHabilitada) {
    botones.push({
      label: "✨ Redactar con IA",
      text: "Escribe la descripción con las notas de la bitácora, los contactos, los equipos y los correos que marques; lo que ya escribiste sirve de guía. Reemplaza la descripción sin preguntar (la línea «Evidencias:» se conserva). Necesita al menos una nota escrita a mano en la bitácora.",
    });
  }
  botones.push(
    {
      label: "Generar link de evidencias (sin contraseña)",
      text: "Arma un ZIP sin contraseña con todas las evidencias del caso —también las fotos de la bitácora y lo que mandó el proveedor— y pone su enlace al final de la descripción, en la línea «Evidencias:». Si ya había un enlace, sale como «Generar otro link» y lo cambia por el nuevo.",
    },
    {
      label: "🔗 Link de evidencias",
      text: "Abre en otra pestaña el ZIP cuyo enlace ya va en la descripción.",
    },
    {
      label: "Bitácora interna · Ver / Ocultar",
      text: "Muestra u oculta las notas escritas a mano en el caso, para apoyarte al describir el problema.",
    },
  );
  if (iaHabilitada) botones.push(...BOTONES_CORREOS_IA);
  botones.push(
    {
      label: "Ver los N IMEIs · Copiar los N",
      text: "En los avisos rojo y ámbar: despliega los IMEIs con problema y los copia uno por renglón, listos para pegarlos en un caso nuevo.",
    },
    {
      label: "Crear en Jimi",
      text: "Crea el ticket en el portal de Jimi al tocarlo: no pide confirmación. Si en Jimi ya hay un ticket con el mismo título que no está ligado a otro caso, liga ese en vez de abrir otro. Sale gris mientras carga, si el caso trae equipos de varios modelos, si Jimi no reconoce algún IMEI o si no cargó la lista de modelos.",
    },
    { label: "Cancelar", text: "Cierra sin crear nada. Lo que escribiste se pierde." },
  );

  const indicadores: ItemAyuda[] = [
    {
      label: "(sugerida / sugerido automáticamente)",
      text: "La etiqueta la propuso el sistema a partir de la descripción y la bitácora; el modelo, a partir de lo que Jimi sabe del IMEI o del modelo del caso. Revísalos.",
    },
    {
      label: "Aviso ámbar: varios modelos",
      text: "Jimi solo acepta tickets de un solo modelo. Separa los equipos en casos de un solo modelo y abre un ticket por cada uno.",
    },
    {
      label: "Aviso rojo: IMEI no reconocido",
      text: "Jimi no tiene registrado ese IMEI. Corrígelo o quítalo del caso para poder abrir el ticket.",
    },
  ];
  if (iaHabilitada) indicadores.push(...FICHAS_CORREOS_IA);

  return {
    manual: "jimi-abrir",
    intro: (
      <p>
        Crea el ticket en el portal de soporte de Jimi desde el caso, con todos sus equipos. El
        título, la descripción y las sugerencias vienen propuestos: revísalos. A la derecha tienes
        la bitácora del caso para apoyarte.
      </p>
    ),
    secciones: [
      { titulo: "Qué hace cada botón", items: botones },
      { titulo: "Qué significa cada indicador", items: indicadores },
    ],
  };
}
